import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BulkEditStatusPopover } from 'src/components/common/BulkActions/BulkEditStatusPopover';
import { MultiSelectionToggleGlobal } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGlobal';
import { FilterToolbar } from 'src/components/FilterToolbar';
import { QuickFilterToolbarItem } from 'src/components/FilterToolbar/QuickFilterToolbarItem';
import { useFiltersHelpers } from 'src/components/FilterToolbar/useFiltersHelpers';
import { ViewModeSelector } from 'src/components/MainFilterBar';
import * as styles from 'src/components/MainFilterBar/MainFilterBar.css';
import {
  purchaseMandatoryFiltersToShow,
  usePurchaseFilters,
} from 'src/components/MainFilterBar/usePurchaseFilters';
import { PurchaseGlobalActionsPermissionsWrapper } from 'src/components/Purchases/PurchaseFilterBar/PurchaseGlobalActionsPermissionsWrapper';
import {
  PLACEHOLDER_EVENTS_QUICK_FILTER_ID,
  useIsPlaceholderEventsOnly,
} from 'src/contexts/CatalogDataContext/usePlaceholderEventsOnly';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import {
  QUICK_FILTER_SEARCH_PARAM_KEY,
  useFilterToolbarQuickFilters,
} from 'src/hooks/useQuickFilters';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { ContentId } from 'src/utils/constants/contentId';
import { PURCHASE_VIEW_MODE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  ActionOutboxEntityType,
  Feature,
  PurchaseOrderQuery,
  PurchaseViewMode,
  UserSetting,
} from 'src/WebApiController';

import { useDefaultPurchaseFilters } from './hooks/useDefaultPurchaseFilters';
import { PURCHASE_VIEW_MODE_TO_ICON } from './PurchaseFilterBar.types';
import { PurchaseSearchBox } from './PurchaseSearchBox';

export function PurchaseFilterBar({
  isEventPage = false,
  returnUrl,
  viagVirtId,
}: {
  isEventPage?: boolean;
  returnUrl?: string;
  viagVirtId?: string;
}) {
  const {
    filterQuery: purchaseQuery,
    initialQuery,
    tempQuery,
    setFilterQuery,
    setInitialQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<PurchaseOrderQuery>();

  useEffect(() => {
    setTempQuery(purchaseQuery);
  }, [purchaseQuery, setTempQuery]);

  const defaultQuickFilters = useDefaultPurchaseFilters({
    purchaseQuery,
    initialQuery,
  });

  // "Placeholder Events" quick filter — a client-side filter (applied in
  // CatalogDataContextV2 via searchEvents) that shares the single-selection
  // quickFilterId URL param, so selecting any other quick filter deselects it.
  const hasPlaceholderEventFeature = useUserHasFeature(
    Feature.PlaceholderEventGroup
  );
  const placeholderEventsOnly = useIsPlaceholderEventsOnly();
  const [, setSearchParams] = useSearchParams();

  const placeholderQuickFilter = useMemo<QuickFilterToolbarItem[]>(
    () =>
      hasPlaceholderEventFeature && !isEventPage
        ? [
            {
              id: PLACEHOLDER_EVENTS_QUICK_FILTER_ID,
              name: (
                <span className={styles.placeholderFilterLabel}>
                  <Content id={ContentId.PlaceholderEvents} />
                </span>
              ),
              isCustomFilter: false,
              isSelected: placeholderEventsOnly,
              onClick: () => {
                // Reset the server query (like the other quick filters) and
                // toggle placeholder via the shared quickFilterId param —
                // selecting another quick filter overwrites it and turns this off.
                setFilterQuery(initialQuery);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (placeholderEventsOnly) {
                    next.delete(QUICK_FILTER_SEARCH_PARAM_KEY);
                  } else {
                    next.set(
                      QUICK_FILTER_SEARCH_PARAM_KEY,
                      PLACEHOLDER_EVENTS_QUICK_FILTER_ID
                    );
                  }
                  return next;
                });
              },
            },
          ]
        : [],
    [
      hasPlaceholderEventFeature,
      isEventPage,
      placeholderEventsOnly,
      initialQuery,
      setFilterQuery,
      setSearchParams,
    ]
  );

  const { value: defaultViewModeUserSetting, setUserSetting } =
    useServerUserSetting<PurchaseViewMode>({
      id: UserSetting.PurchasePageViewMode,
      currentLoginUserOnly: true,
    });

  const filterToolbarProps = useFilterToolbarQuickFilters({
    quickFiltersStateSetting: UserSetting.QuickFiltersStatePurchases,
    customQuickFiltersSetting: UserSetting.QuickFiltersCustomPurchases,
    defaultQuickFilters,
    currentQuery: purchaseQuery,
    initialQuery,
    onSelect: ({ id, query }) => {
      if (query.viewMode != null) {
        // We need to update initialQuery, otherwise the quick filter "All"
        // may change the current view mode
        if (query.viewMode !== initialQuery.viewMode) {
          setInitialQuery({ ...initialQuery, viewMode: query.viewMode });
        }
      }
      setFilterQuery(query);

      // This needs to happen after to reduce re-rendering
      if (query.viewMode != null) {
        // Update user setting view mode too so it won't revert the filter query viewMode
        if (query.viewMode !== defaultViewModeUserSetting) {
          setUserSetting(query.viewMode);
        }
      }
    },
  });

  const isMobile = useMatchMedia('mobile');

  const purchaseFilters = usePurchaseFilters({});

  const { appliedFilters } = useFiltersHelpers({
    filters: purchaseFilters,
    activeQuery: purchaseQuery,
    mandatoryFiltersToShow: purchaseMandatoryFiltersToShow,
    initialQuery,
  });

  return (
    <div className={styles.mainFilterBarDiv}>
      <div className={styles.dockLeftDiv}>
        <div className={styles.mainFilterBarLeftChild}>
          {purchaseQuery && (
            <FilterToolbar
              {...filterToolbarProps}
              additionalQuickFilters={placeholderQuickFilter}
              filterAppliedCounts={appliedFilters.length}
              onSubmitFilter={() => {
                setFilterQuery(tempQuery);
              }}
              mandatoryFiltersToShow={purchaseMandatoryFiltersToShow}
              filters={purchaseFilters}
              filterAppliedIds={appliedFilters}
              resetTempQuery={resetTempQuery}
              setTempQuery={(partialQuery) => {
                setTempQuery({
                  ...tempQuery,
                  ...partialQuery,
                } as PurchaseOrderQuery);
              }}
              tempQuery={tempQuery}
            />
          )}
        </div>
      </div>
      <div className={styles.dockRightDiv}>
        <div className={styles.mainFilterBarRightChild}>
          {!isMobile && (
            <>
              {!isEventPage && (
                <>
                  <BulkEditStatusPopover
                    entityType={ActionOutboxEntityType.Purchase}
                  />
                  <ViewModeSelector
                    query={purchaseQuery}
                    entityType={ActionOutboxEntityType.Purchase}
                    initialQuery={initialQuery}
                    setFilterQuery={setFilterQuery}
                    setInitialQuery={setInitialQuery}
                    viewModeSettingsId={UserSetting.PurchasePageViewMode}
                    viewModeCidMap={PURCHASE_VIEW_MODE_TO_CID}
                    viewModeIconMap={PURCHASE_VIEW_MODE_TO_ICON}
                  />
                  <PurchaseGlobalActionsPermissionsWrapper>
                    <MultiSelectionToggleGlobal />
                  </PurchaseGlobalActionsPermissionsWrapper>
                </>
              )}

              {isEventPage && <PurchaseSearchBox isEventPage={isEventPage} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
